import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useIsPlatformAdmin } from '../../../../hooks/useAuth'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { EmptyState } from '../../../../components/ui/empty-state'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select'
import { BillingInvoicesTable } from '../../../../components/admin/BillingInvoicesTable'
import {
  formatCents,
  useAdminBillingInvoices,
  useAdminBillingUsage,
  type AdminBillingInvoiceFilter,
} from '../../../../hooks/use-admin-billing'

export const Route = createFileRoute('/admin/billing/invoices/')({
  component: RouteComponent,
})

const ALL = 'all'
const STATES = ['draft', 'due', 'overdue', 'paid', 'cancelled'] as const

// The invoices of every organization, with what is due and overdue (spec
// 2026-10-05-14). Platform admins only.
function RouteComponent() {
  const { t, i18n } = useTranslation('admin')
  const { loading, orgCode } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const navigate = useNavigate({ from: '/admin/billing/invoices/' })
  const [org, setOrg] = useState(ALL)
  const [state, setState] = useState(ALL)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  useEffect(() => {
    if (!loading && !isPlatformAdmin) {
      navigate({ to: '/', replace: true })
    }
  }, [loading, isPlatformAdmin, navigate])

  const filter: AdminBillingInvoiceFilter = {}
  if (org !== ALL) filter.organization_uid = org
  if (state !== ALL) filter.payment_state = state as (typeof STATES)[number]
  if (from) filter.from = `${from}-01`
  if (to) filter.to = `${to}-01`
  const { data, isLoading, error } = useAdminBillingInvoices(filter)
  const { payment_state: _ignored, ...chipFilter } = filter
  const chipData = useAdminBillingInvoices(chipFilter).data?.data
  const usage = useAdminBillingUsage()
  const chips = (['draft', 'due', 'overdue'] as const).map((s) => {
    const list = (chipData ?? []).filter((i) => i.payment_state === s)
    const cents = list.reduce((sum, i) => sum + (s === 'draft' ? i.total_cents : i.remaining_cents), 0)
    return { state: s, count: list.length, cents }
  })
  const rows = data?.data ?? []

  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('billing.title'), path: '/admin/billing' },
    { title: t('billing.invoices.title') },
  ])

  if (loading || !isPlatformAdmin) {
    return null
  }

  return (
    <div className="space-y-6" data-testid="admin-billing-invoices-page">
      <PageHeader icon={SECTION_ICONS.adminBilling} title={t('billing.invoices.title')} description={t('billing.invoices.subtitle')} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-sm text-muted-foreground">{t('billing.invoices.total_due')}</div>
          <div className="text-2xl font-semibold tabular-nums" data-testid="billing-total-due">
            {formatCents(data?.total_due_cents ?? 0, i18n.language)}
          </div>
        </div>
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="text-sm text-muted-foreground">{t('billing.invoices.total_overdue')}</div>
          <div className="text-2xl font-semibold tabular-nums text-destructive" data-testid="billing-total-overdue">
            {formatCents(data?.total_overdue_cents ?? 0, i18n.language)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="billing-invoice-chips">
        {chips.map((c) => (
          <button
            key={c.state}
            type="button"
            disabled={c.count === 0}
            aria-pressed={state === c.state}
            onClick={() => setState(state === c.state ? ALL : c.state)}
            className={`rounded-lg border px-4 py-2 text-left text-sm disabled:opacity-50 ${state === c.state ? 'border-primary bg-primary/10' : 'border-border bg-card'}`}
            data-testid={`billing-chip-${c.state}`}
          >
            <div className="font-medium" data-testid={`billing-chip-${c.state}-label`}>
              {t(`billing.invoices.chip.${c.state}`, { count: c.count })}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">{formatCents(c.cents, i18n.language)}</div>
          </button>
        ))}
      </div>

      <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid="billing-invoice-filters">
        <div className="space-y-1">
          <Label>{t('billing.invoices.column.organization')}</Label>
          <Select value={org} onValueChange={setOrg}>
            <SelectTrigger className="w-60" data-testid="billing-filter-organization">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('billing.invoices.all')}</SelectItem>
              {(usage.data?.data ?? []).map((o) => (
                <SelectItem key={o.organization_uid} value={o.organization_uid}>
                  {o.organization_name || o.organization_code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('billing.invoices.column.state')}</Label>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-44" data-testid="billing-filter-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('billing.invoices.all')}</SelectItem>
              {STATES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`billing.invoices.state.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('billing.invoices.from')}</Label>
          <Input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" data-testid="billing-filter-from" />
        </div>
        <div className="space-y-1">
          <Label>{t('billing.invoices.to')}</Label>
          <Input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" data-testid="billing-filter-to" />
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="billing-error">
          {t('billing.invoices.load_error')}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('billing.loading')}</div>
        ) : rows.length === 0 ? (
          <EmptyState variant="inline" live className="py-8" title={t('billing.invoices.empty')} data-testid="billing-invoices-empty" />
        ) : (
          <BillingInvoicesTable invoices={rows} showOrganization />
        )}
      </div>
    </div>
  )
}
